/*
================================================================================
File: src/pages/SqueezeSite.js
Description: Component for managing the user's squeeze site.
================================================================================
*/
import React, { useState } from "react";
import instance from "../api/axios";
import PageContent from "../components/common/PageContent";

// SVG Icons
const GlobeIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
      clipRule="evenodd"
    />
  </svg>
);

const PencilIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
  </svg>
);

const CodeIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
      clipRule="evenodd"
    />
  </svg>
);

const RocketIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
    <path d="M10.5 1.5H9.5A1.5 1.5 0 008 3v2H4a2 2 0 00-2 2v7a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-4V3a1.5 1.5 0 00-1.5-1.5z" />
  </svg>
);

// ActionCard component
const ActionCard = ({ icon: Icon, title, description, accent, onClick }) => (
  <div
    className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 text-center hover:shadow-lg transition-shadow cursor-pointer"
    onClick={onClick}
  >
    <div className={`p-3 rounded-lg ${accent} mb-4 w-fit mx-auto`}>
      <Icon />
    </div>
    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
      {title}
    </h3>
    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
      {description}
    </p>
  </div>
);

const SqueezSite = ({ pulldata, user }) => {
  const [co_name, setCo_Name] = useState(user.co_name);
  const [coemail, setCoemail] = useState(user.email);
  const [phone, setPhone] = useState(user.phone);
  const [loading, setLoading] = useState(false);
  const [domain, setDomain] = useState(user.site);
  const [logoFile, setLogoFile] = useState(null);
  const [contentToDisplay, setContentToDisplay] = useState("1");
  const [error, setError] = useState(null);

  const handleFormSubmit = async (url) => {
    setLoading(true);
    const formData = new FormData();
    if (logoFile) {
      formData.append("logo", logoFile);
    }
    formData.append("IFS", domain);
    formData.append("sellername", co_name);
    formData.append("mailto", coemail);
    formData.append("contactNumber", phone);
    formData.append("owner", user.app_id);
    try {
      await instance.post(url, formData);
      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError(`Error submitting form: ` + e.response.data.message);
        console.error(`Error submitting form: `, e.response.data.message);
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      setLoading(false);
      setContentToDisplay("1");
      await pulldata(user?.app_id);
    }
  };

  const handleCreate = (e) => {
    e.preventDefault();
    handleFormSubmit("/create.php");
  };

  const handleEdit = (e) => {
    e.preventDefault();
    handleFormSubmit("/create.php");
  };

  const handleDelete = (e) => {
    e.preventDefault();
    handleFormSubmit("/deleteSellerSite.php");
  };

  const renderContent = () => {
    switch (contentToDisplay) {
      case "2": // Edit Site
        return (
          <form onSubmit={handleEdit}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                Edit Your Squeeze Site
              </h3>
              <button
                type="button"
                onClick={() => setContentToDisplay("1")}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 text-2xl"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Seller Web Domain
                </label>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  className="mt-1 block w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Seller Company Business Name
                </label>
                <input
                  type="text"
                  value={co_name}
                  onChange={(e) => setCo_Name(e.target.value)}
                  className="mt-1 block w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Seller Company Contact Email
                </label>
                <input
                  type="email"
                  value={coemail}
                  onChange={(e) => setCoemail(e.target.value)}
                  className="mt-1 block w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Seller Company Contact Number
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-1 block w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Seller Site Logo (* Optional)
                </label>
                <input
                  type="file"
                  onChange={(e) => setLogoFile(e.target.files[0])}
                  className="mt-1 block w-full text-sm text-gray-600 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 dark:file:bg-blue-900/30 file:text-blue-600 dark:file:text-blue-400 hover:file:bg-blue-100 dark:hover:file:bg-blue-900/50"
                />
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-8">
              <button
                type="button"
                onClick={() => setContentToDisplay("1")}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        );
      case "3": // Create Site
        return (
          <form onSubmit={handleCreate}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                Create Your Squeeze Site
              </h3>
              <button
                type="button"
                onClick={() => setContentToDisplay("1")}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 text-2xl"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4 ">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Seller Web Domain
                </label>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  className="mt-1 block w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Seller Company Business Name
                </label>
                <input
                  type="text"
                  value={co_name}
                  onChange={(e) => setCo_Name(e.target.value)}
                  className="mt-1 block w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Seller Company Contact Email
                </label>
                <input
                  type="email"
                  value={coemail}
                  onChange={(e) => setCoemail(e.target.value)}
                  className="mt-1 block w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Seller Company Contact Number
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-1 block w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Seller Site Logo (* Optional)
                </label>
                <input
                  type="file"
                  onChange={(e) => setLogoFile(e.target.files[0])}
                  className="mt-1 block w-full text-sm dark:bg-gray-800 dark:text-gray-200"
                />
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-8">
              <button
                type="button"
                onClick={() => setContentToDisplay("1")}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? "Creating..." : "Create Site"}
              </button>
            </div>
          </form>
        );
      case "4": // Delete Site
        return (
          <div>
            <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
              Delete Your Site
            </h3>
            <p className="text-red-600 dark:text-red-400 font-semibold mb-3">
              ⚠️ Warning
            </p>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              This action is permanent and cannot be undone. All your squeeze
              site data will be lost. Are you sure you want to delete your
              seller site?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setContentToDisplay("1")}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 rounded-md font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? "Deleting..." : "Confirm Deletion"}
              </button>
            </div>
          </div>
        );
      default: // Advanced Features
        return (
          <div className="">
            <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              Advanced Features
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Explore advanced features and integrations for your Squeeze Site.
            </p>
            <div className="space-y-8">
              <div className="border-l-4 border-purple-400 pl-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-purple-600 text-white text-sm font-bold">
                    1
                  </span>
                  <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                    Add HTML to Your Website
                  </h4>
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  Add the following HTML code where you want the input form to
                  appear on your website.
                </p>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Option A: With "Enter Your Information" Header
                </p>
                <code className="block bg-gray-900 text-green-300 font-mono text-xs p-4 rounded-lg overflow-x-visiblemb-3 border border-gray-700">
                  &lt;div id="sellerForm2"&gt;&lt;/div&gt;
                </code>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Option B: Without Header
                </p>
                <code className="block bg-gray-900 text-green-300 font-mono text-xs p-4 rounded-lg overflow-x-visibleborder border-gray-700">
                  &lt;div id="sellerForm"&gt;&lt;/div&gt;
                </code>
              </div>

              <div className="border-l-4 border-indigo-400 pl-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-indigo-600 text-white text-sm font-bold">
                    2
                  </span>
                  <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                    Add Script Tag Before &lt;/body&gt;
                  </h4>
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  Add the following code at the bottom of your webpage, right
                  above the closing &lt;/body&gt; tag.
                </p>
                <code className="block bg-gray-900 text-green-300 font-mono text-xs p-4 rounded-lg overflow-x-visibleborder border-gray-700">
                  &lt;script
                  src="https://fastcashforkeys.com/assets/js/pocketquery.js?key=
                  {user?.app_api_key}"&gt;&lt;/script&gt;
                </code>
              </div>

              <div className="border-l-4 border-blue-400 pl-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold">
                    3
                  </span>
                  <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                    Add Stylesheet (Optional)
                  </h4>
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  To style the form, add this stylesheet link below the
                  &lt;head&gt; tag.
                </p>
                <code className="block bg-gray-900 text-green-300 font-mono text-xs p-4 rounded-lg overflow-x-visibleborder border-gray-700">
                  &lt;link
                  href="https://fastcashforkeys.com/assets/css/theme.css"
                  rel="stylesheet"&gt;
                </code>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <PageContent title="Squeeze Site" error={error}>
      <div className="p-8 space-y-6">
        {/* Status Hero Banner */}
        {user?.site ? (
          <div className="bg-green-600 rounded-xl p-6 text-white shadow-lg flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-lg">
                <GlobeIcon />
              </div>
              <div>
                <h2 className="text-2xl font-bold">
                  Your Squeeze Site Is Live
                </h2>
                <p className="text-green-100 text-sm">
                  fastcashforkeys.com/{user.site}
                </p>
              </div>
            </div>
            <button
              onClick={() =>
                window.open(
                  `https://fastcashforkeys.com/${user?.site}`,
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              className="px-5 py-2 bg-white text-green-700 font-semibold rounded-lg hover:bg-green-50 transition-colors whitespace-nowrap"
            >
              Visit Site →
            </button>
          </div>
        ) : (
          <div className="bg-blue-600 rounded-xl p-8 text-white shadow-lg">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-white/20 rounded-lg">
                <RocketIcon />
              </div>
              <h2 className="text-2xl font-bold">Set Up Your Squeeze Site</h2>
            </div>
            <p className="text-blue-100 text-lg mb-6">
              Create your own lead capture page and start collecting leads from
              your website.
            </p>
            <button
              onClick={() => setContentToDisplay("3")}
              className="px-5 py-2 bg-white text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition-colors"
            >
              Get Started →
            </button>
          </div>
        )}

        {/* Action Cards (only when site exists) */}
        {user?.site && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <ActionCard
              icon={PencilIcon}
              title="Site Admin"
              description="Edit your site settings and information."
              accent="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
              onClick={() => setContentToDisplay("2")}
            />
            <ActionCard
              icon={CodeIcon}
              title="Advanced Features"
              description="View integration and embed instructions."
              accent="bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400"
              onClick={() => setContentToDisplay("1")}
            />
            <ActionCard
              icon={TrashIcon}
              title="Delete My Site"
              description="Permanently remove your squeeze site."
              accent="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400"
              onClick={() => setContentToDisplay("4")}
            />
          </div>
        )}

        {/* Content Panel */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-md">
          {renderContent()}
        </div>
      </div>
    </PageContent>
  );
};

export default SqueezSite;
